import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useSession } from '../context/SessionContext';
import './Dashboard.css';

const fechaLocal = (fecha) => {
  const y = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, '0');
  const d = String(fecha.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const hoy = new Date();
const RANGO_INICIAL = {
  desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
  hasta: fechaLocal(hoy)
};

const dinero = (valor, decimales = 0) =>
  Number(valor || 0).toLocaleString('es-EC', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  });

const numero = (valor) => Number(valor || 0).toLocaleString('es-EC', { maximumFractionDigits: 0 });

const porcentajeCambio = (actual, anterior) => {
  const base = Number(anterior || 0);
  const valor = Number(actual || 0);
  if (!base) return valor ? null : 0;
  return ((valor - base) / Math.abs(base)) * 100;
};

function Icono({ tipo }) {
  const paths = {
    tallos: (
      <path d="M12 21V9m0 5c-4.5 0-7-2.3-7-6.5C9.5 7.5 12 9.8 12 14Zm0-3c4.5 0 7-2.3 7-6.5-4.5 0-7 2.3-7 6.5Z" />
    ),
    compra: <path d="M6 8h12l-1 12H7L6 8Zm3 0V6a3 3 0 0 1 6 0v2" />,
    venta: (
      <path d="M12 2v20m5-16.5c-1.1-1-2.7-1.5-5-1.5-2.8 0-5 1.4-5 3.5s2 3 5 3.5 5 1.4 5 3.5-2.2 3.5-5 3.5c-2.3 0-4-.6-5.2-1.7" />
    ),
    precioCompra: (
      <>
        <path d="M4 7h10l3 4-7 7-6-6V7Z" />
        <circle cx="8" cy="10" r="1" />
        <path d="M14 5h4a2 2 0 0 1 2 2v4" />
      </>
    ),
    precioVenta: (
      <>
        <path d="M12 3v18m4-14c-.9-.8-2.2-1.2-4-1.2-2.2 0-4 1.1-4 2.8 0 1.6 1.6 2.3 4 2.7s4 1.1 4 2.8-1.8 2.8-4 2.8c-1.8 0-3.2-.5-4.2-1.4" />
        <path d="m18 4 2 2 3-3" />
      </>
    ),
    utilidad: <path d="m4 17 5-5 4 4 7-8m-5 0h5v5" />
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {paths[tipo]}
    </svg>
  );
}

function Tarjeta({ tipo, etiqueta, valor, actual, anterior, detalle, invertirTendencia = false }) {
  const cambio = porcentajeCambio(actual, anterior);
  const mejora = cambio === null || (invertirTendencia ? cambio <= 0 : cambio >= 0);
  return (
    <article className={`commercial-card commercial-card--${tipo}`}>
      <div className="commercial-card__head">
        <span>{etiqueta}</span>
        <span className="commercial-card__icon">
          <Icono tipo={tipo} />
        </span>
      </div>
      <strong>{valor}</strong>
      <div className="commercial-card__foot">
        <span className={mejora ? 'trend trend--up' : 'trend trend--down'}>
          {cambio === null ? 'Nuevo' : `${cambio >= 0 ? '↑' : '↓'} ${Math.abs(cambio).toFixed(1)}%`}
        </span>
        <span>{detalle}</span>
      </div>
    </article>
  );
}

function Dashboard() {
  const { user } = useSession();
  const [filtros, setFiltros] = useState(RANGO_INICIAL);
  const [aplicado, setAplicado] = useState(RANGO_INICIAL);
  const [data, setData] = useState({ resumen: {}, anterior: {}, diario: [], productos: [] });
  const [metrica, setMetrica] = useState('venta');
  const [ranking, setRanking] = useState('utilidad');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(async (rango, mostrarCarga = true) => {
    try {
      if (mostrarCarga) setLoading(true);
      setError('');
      const { data: respuesta } = await axios.get('/api/facturas/dashboard-comercial', {
        params: rango,
        headers: { Accept: 'application/json' }
      });
      setData({
        resumen: respuesta?.resumen || {},
        anterior: respuesta?.anterior || {},
        diario: Array.isArray(respuesta?.diario) ? respuesta.diario : [],
        productos: Array.isArray(respuesta?.productos) ? respuesta.productos : []
      });
      setAplicado(rango);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo cargar la información comercial.');
    } finally {
      if (mostrarCarga) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar(RANGO_INICIAL);
  }, [cargar]);

  useEffect(() => {
    const intervalo = setInterval(() => cargar(aplicado, false), 60000);
    return () => clearInterval(intervalo);
  }, [aplicado, cargar]);

  const diarioCompleto = useMemo(() => {
    const porFecha = new Map(data.diario.map((fila) => [fila.fecha, fila]));
    const filas = [];
    const cursor = new Date(`${aplicado.desde}T12:00:00`);
    const fin = new Date(`${aplicado.hasta}T12:00:00`);
    while (cursor <= fin) {
      const fecha = fechaLocal(cursor);
      const fila = porFecha.get(fecha) || { fecha, tallos: 0, compra: 0, venta: 0, utilidad: 0 };
      filas.push({ ...fila, etiqueta: fecha.slice(8, 10) + '/' + fecha.slice(5, 7) });
      cursor.setDate(cursor.getDate() + 1);
    }
    return filas;
  }, [data.diario, aplicado]);

  const productos = useMemo(
    () =>
      [...data.productos]
        .sort((a, b) => Number(b[ranking] || 0) - Number(a[ranking] || 0))
        .slice(0, 8),
    [data.productos, ranking]
  );
  const maxRanking = Math.max(1, ...productos.map((p) => Math.max(0, Number(p[ranking] || 0))));
  const resumen = data.resumen;
  const anterior = data.anterior;
  const colorGrafico =
    metrica === 'venta' ? '#6f46e8' : metrica === 'compra' ? '#f59e55' : '#17a673';
  const nombreMetrica = { venta: 'Ventas', compra: 'Compras', utilidad: 'Utilidad' }[metrica];

  const aplicarFiltros = (event) => {
    event.preventDefault();
    if (filtros.desde > filtros.hasta) {
      setError('La fecha desde no puede ser mayor que la fecha hasta.');
      return;
    }
    cargar({ ...filtros });
  };

  return (
    <div className="commercial-dashboard">
      <header className="commercial-header">
        <div>
          <span className="commercial-kicker">PANEL COMERCIAL</span>
          <h1>Hola, {user?.nombre?.split(' ')[0] || 'Usuario'}</h1>
          <p>Así se está moviendo la compra, venta y rentabilidad de Fresh Exports.</p>
        </div>
        <form className="commercial-filters" onSubmit={aplicarFiltros}>
          <label>
            <span>Desde</span>
            <input
              type="date"
              value={filtros.desde}
              max={filtros.hasta}
              onChange={(e) => setFiltros((f) => ({ ...f, desde: e.target.value }))}
            />
          </label>
          <label>
            <span>Hasta</span>
            <input
              type="date"
              value={filtros.hasta}
              min={filtros.desde}
              onChange={(e) => setFiltros((f) => ({ ...f, hasta: e.target.value }))}
            />
          </label>
          <button type="submit" disabled={loading}>
            {loading ? 'Cargando…' : 'Aplicar'}
          </button>
        </form>
      </header>

      {error && (
        <div className="commercial-alert" role="alert">
          {error}
        </div>
      )}

      <section className="commercial-summary" aria-label="Resumen comercial">
        <Tarjeta
          tipo="tallos"
          etiqueta="Tallos vendidos"
          valor={numero(resumen.tallos)}
          actual={resumen.tallos}
          anterior={anterior.tallos}
          detalle={`${numero(resumen.facturas)} facturas`}
        />
        <Tarjeta
          tipo="compra"
          etiqueta="Compra total"
          valor={dinero(resumen.compra)}
          actual={resumen.compra}
          anterior={anterior.compra}
          detalle="costo de productos"
        />
        <Tarjeta
          tipo="precioCompra"
          etiqueta="Promedio compra"
          valor={dinero(resumen.precioPromedioCompra, 3)}
          actual={resumen.precioPromedioCompra}
          anterior={anterior.precioPromedioCompra}
          detalle="precio por tallo"
          invertirTendencia
        />
        <Tarjeta
          tipo="venta"
          etiqueta="Venta total"
          valor={dinero(resumen.venta)}
          actual={resumen.venta}
          anterior={anterior.venta}
          detalle="facturación bruta"
        />
        <Tarjeta
          tipo="precioVenta"
          etiqueta="Promedio venta"
          valor={dinero(resumen.precioPromedioVenta, 3)}
          actual={resumen.precioPromedioVenta}
          anterior={anterior.precioPromedioVenta}
          detalle="precio por tallo"
        />
        <Tarjeta
          tipo="utilidad"
          etiqueta="Utilidad"
          valor={dinero(resumen.utilidad)}
          actual={resumen.utilidad}
          anterior={anterior.utilidad}
          detalle={`margen ${Number(resumen.margen || 0).toFixed(1)}%`}
        />
      </section>

      <section className="commercial-grid">
        <article className="commercial-panel commercial-chart-panel">
          <div className="commercial-panel__head">
            <div>
              <span className="commercial-kicker">RENDIMIENTO DIARIO</span>
              <h2>Evolución comercial</h2>
              <p>
                <strong>{dinero(resumen[metrica])}</strong> {nombreMetrica.toLowerCase()} en el
                período
              </p>
            </div>
            <div className="commercial-tabs" aria-label="Métrica del gráfico">
              {['venta', 'compra', 'utilidad'].map((item) => (
                <button
                  key={item}
                  type="button"
                  className={metrica === item ? 'active' : ''}
                  onClick={() => setMetrica(item)}
                >
                  {{ venta: 'Ventas', compra: 'Compras', utilidad: 'Utilidad' }[item]}
                </button>
              ))}
            </div>
          </div>
          <div className="commercial-chart">
            {loading ? (
              <div className="commercial-empty">Cargando resultados…</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={diarioCompleto} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="commercialGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colorGrafico} stopOpacity={0.28} />
                      <stop offset="95%" stopColor={colorGrafico} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e9e8f2" strokeDasharray="4 5" vertical={false} />
                  <XAxis
                    dataKey="etiqueta"
                    tick={{ fill: '#8b93a7', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fill: '#8b93a7', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={68}
                    tickFormatter={(v) => dinero(v)}
                  />
                  <Tooltip
                    formatter={(v) => [dinero(v, 2), nombreMetrica]}
                    labelFormatter={(_, items) => items?.[0]?.payload?.fecha || ''}
                    contentStyle={{
                      border: 0,
                      borderRadius: 12,
                      boxShadow: '0 12px 35px rgba(36, 43, 72, .15)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey={metrica}
                    stroke={colorGrafico}
                    strokeWidth={3}
                    fill="url(#commercialGradient)"
                    activeDot={{ r: 5, strokeWidth: 3, fill: '#fff' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </article>

        <article className="commercial-panel commercial-products">
          <div className="commercial-panel__head">
            <div>
              <span className="commercial-kicker">VENTAS POR PRODUCTO</span>
              <h2>Productos destacados</h2>
            </div>
            <div className="commercial-tabs">
              <button
                type="button"
                className={ranking === 'venta' ? 'active' : ''}
                onClick={() => setRanking('venta')}
              >
                Ventas
              </button>
              <button
                type="button"
                className={ranking === 'utilidad' ? 'active' : ''}
                onClick={() => setRanking('utilidad')}
              >
                Rentabilidad
              </button>
            </div>
          </div>
          <div className="product-list">
            {!loading && !productos.length ? (
              <div className="commercial-empty">No hay ventas en este período.</div>
            ) : (
              productos.map((producto, index) => (
                <div className="product-row" key={`${producto.idproducto}-${producto.producto}`}>
                  <div className="product-row__title">
                    <span className="product-rank">{index + 1}</span>
                    <strong title={producto.producto}>{producto.producto}</strong>
                    <b>{dinero(producto[ranking])}</b>
                  </div>
                  <div className="product-bar">
                    <span
                      style={{
                        width: `${Math.max(3, (Math.max(0, Number(producto[ranking] || 0)) / maxRanking) * 100)}%`
                      }}
                    />
                  </div>
                  <div className="product-row__numbers">
                    <span>
                      Compra <b>{dinero(producto.compra)}</b>
                    </span>
                    <span>
                      Venta <b>{dinero(producto.venta)}</b>
                    </span>
                    <span>
                      Utilidad <b>{dinero(producto.utilidad)}</b>
                    </span>
                    <span>
                      Margen <b>{Number(producto.margen || 0).toFixed(1)}%</b>
                    </span>
                    <span className="product-average product-average--buy">
                      Prom. compra <b>{dinero(producto.precioPromedioCompra, 3)}</b>
                    </span>
                    <span className="product-average product-average--sale">
                      Prom. venta <b>{dinero(producto.precioPromedioVenta, 3)}</b>
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </article>
      </section>
    </div>
  );
}

export default Dashboard;
